import { queryOptions, skipToken, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  buildAttributesQuery,
  buildAttributeTotalsQuery,
  buildEventTotalsQuery,
  buildEventsQuery,
  dataIndexKeys,
  type AttributeScope,
  type DataIndexAttribute,
  type DataIndexAttributeFilters,
  type DataIndexAttributePage,
  type DataIndexAttributeUsage,
  type DataIndexEvent,
  type DataIndexEventFilters,
  type DataIndexEventPage,
  type DataIndexEventUsage,
  type DataIndexObjectAttributeUsage,
  type DataIndexListFilters,
  type DataIndexNarrowing,
  type DataIndexTotals,
} from './entity';

/**
 * Reads behind the data index.
 *
 * Every read is always-fresh: the rows describe data written by ingestion, so
 * a cached page can be wrong the moment it is shown, and the list is
 * search/filter/sort/paged besides. The list, the totals and the filter
 * catalogue are three separate requests against the same narrowing — the list
 * endpoint deliberately omits a grand total, which is what the totals read is
 * for.
 */

/** Rows change as ingestion writes them; never serve one from cache alone. */
const STALE_TIME = 0;

/**
 * The tag and source catalogues a filter bar offers. These change on the
 * order of days, so re-requesting them on every mount of the bar buys
 * nothing.
 */
const FILTER_STALE_TIME = 60_000;

/** Sentinel keys sit outside the workspace subtree; `skipToken` keeps them empty. */
const INACTIVE_KEY = {
  attributeList: ['data-index', 'attributes', 'list', 'inactive'] as const,
  eventList: ['data-index', 'events', 'list', 'inactive'] as const,
};

function environmentPath(workspaceId: number) {
  return { environment_id: String(workspaceId) };
}

/**
 * The URL's workspace id while the read is enabled and the id is a positive
 * integer, otherwise null. Reactive, so a workspace switch re-renders.
 */
function useActiveWorkspaceId(enabled: boolean): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return enabled && workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/* ------------------------------------------------------------------------- */
/* Lists                                                                      */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getDataIndexAttributes(
  workspaceId: number,
  scope: AttributeScope,
  filters: DataIndexListFilters,
): Promise<DataIndexAttributePage> {
  const raw = await hydraUiApi.attributes.list({
    params: { path: environmentPath(workspaceId), query: buildAttributesQuery(filters, scope) },
  });
  return { attributes: raw.attributes, tags: raw.tags ?? [] };
}

/** @public */
export const getDataIndexAttributesQueryOptions = (
  workspaceId: number,
  scope: AttributeScope,
  filters: DataIndexListFilters,
) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeList(workspaceId, scope, filters),
    queryFn: () => getDataIndexAttributes(workspaceId, scope, filters),
    staleTime: STALE_TIME,
  });

type AttributeListOptions = {
  scope: AttributeScope;
  filters: DataIndexListFilters;
  enabled?: boolean;
};

/**
 * One page of attribute rows for a scope. Non-suspending, so the table can
 * keep its chrome and show its own loading state while a filter change loads.
 *
 * @public
 */
export const useDataIndexAttributesOptional = ({ scope, filters, enabled = true }: AttributeListOptions) => {
  const workspaceId = useActiveWorkspaceId(enabled);
  return useQuery({
    queryKey:
      workspaceId != null ? dataIndexKeys.attributeList(workspaceId, scope, filters) : INACTIVE_KEY.attributeList,
    queryFn: workspaceId != null ? () => getDataIndexAttributes(workspaceId, scope, filters) : skipToken,
    staleTime: STALE_TIME,
  });
};

/** @public */
export async function getDataIndexEvents(
  workspaceId: number,
  filters: DataIndexListFilters,
): Promise<DataIndexEventPage> {
  const raw = await hydraUiApi.events.getEventNames({
    params: { path: environmentPath(workspaceId), query: buildEventsQuery(filters) },
  });
  return { events: raw.events, tags: raw.tags ?? [] };
}

/** @public */
export const getDataIndexEventsQueryOptions = (workspaceId: number, filters: DataIndexListFilters) =>
  queryOptions({
    queryKey: dataIndexKeys.eventList(workspaceId, filters),
    queryFn: () => getDataIndexEvents(workspaceId, filters),
    staleTime: STALE_TIME,
  });

type EventListOptions = {
  filters: DataIndexListFilters;
  enabled?: boolean;
};

/** One page of event rows. Non-suspending, like the attribute list. @public */
export const useDataIndexEventsOptional = ({ filters, enabled = true }: EventListOptions) => {
  const workspaceId = useActiveWorkspaceId(enabled);
  return useQuery({
    queryKey: workspaceId != null ? dataIndexKeys.eventList(workspaceId, filters) : INACTIVE_KEY.eventList,
    queryFn: workspaceId != null ? () => getDataIndexEvents(workspaceId, filters) : skipToken,
    staleTime: STALE_TIME,
  });
};

/* ------------------------------------------------------------------------- */
/* Totals                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getDataIndexAttributeTotals(
  workspaceId: number,
  scope: AttributeScope,
  narrowing: DataIndexNarrowing,
): Promise<DataIndexTotals> {
  const raw = await hydraUiApi.attributes.getAttributesTotals({
    params: { path: environmentPath(workspaceId), query: buildAttributeTotalsQuery(narrowing, scope) },
  });
  return raw.totals;
}

/** @public */
export const getDataIndexAttributeTotalsQueryOptions = (
  workspaceId: number,
  scope: AttributeScope,
  narrowing: DataIndexNarrowing,
) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeTotals(workspaceId, scope, narrowing),
    queryFn: () => getDataIndexAttributeTotals(workspaceId, scope, narrowing),
    staleTime: STALE_TIME,
  });

/** @public */
export async function getDataIndexEventTotals(
  workspaceId: number,
  narrowing: DataIndexNarrowing,
): Promise<DataIndexTotals> {
  const raw = await hydraUiApi.events.getEventNamesTotals({
    params: { path: environmentPath(workspaceId), query: buildEventTotalsQuery(narrowing) },
  });
  return raw.totals;
}

/** @public */
export const getDataIndexEventTotalsQueryOptions = (workspaceId: number, narrowing: DataIndexNarrowing) =>
  queryOptions({
    queryKey: dataIndexKeys.eventTotals(workspaceId, narrowing),
    queryFn: () => getDataIndexEventTotals(workspaceId, narrowing),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* Filter catalogues                                                          */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getDataIndexAttributeFilters(
  workspaceId: number,
  scope: AttributeScope,
): Promise<DataIndexAttributeFilters> {
  return hydraUiApi.dataIndex.getAttributeFilters({
    params: {
      path: environmentPath(workspaceId),
      query:
        scope.kind === 'profile' ? {} : { type_id: scope.objectTypeId, relationship: scope.kind === 'relationship' },
    },
  });
}

/** @public */
export const getDataIndexAttributeFiltersQueryOptions = (workspaceId: number, scope: AttributeScope) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeFilters(workspaceId, scope),
    queryFn: () => getDataIndexAttributeFilters(workspaceId, scope),
    staleTime: FILTER_STALE_TIME,
  });

/** @public */
export async function getDataIndexEventFilters(workspaceId: number): Promise<DataIndexEventFilters> {
  return hydraUiApi.dataIndex.getEventFilters({ params: { path: environmentPath(workspaceId) } });
}

/** @public */
export const getDataIndexEventFiltersQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: dataIndexKeys.eventFilters(workspaceId),
    queryFn: () => getDataIndexEventFilters(workspaceId),
    staleTime: FILTER_STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* Detail                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getDataIndexAttribute(
  workspaceId: number,
  scope: AttributeScope,
  name: string,
): Promise<DataIndexAttribute> {
  const path = environmentPath(workspaceId);

  switch (scope.kind) {
    case 'profile': {
      const raw = await hydraUiApi.attributes.get({ params: { path: { ...path, attribute: name } } });
      return raw.attribute;
    }
    case 'object': {
      const raw = await hydraUiApi.objectAttributes.get({
        params: { path: { ...path, type_id: String(scope.objectTypeId), attribute: name } },
      });
      return raw.attribute;
    }
    case 'relationship': {
      const raw = await hydraUiApi.relationshipAttributes.getObjectRelationshipAttributes({
        params: { path: { ...path, type_id: String(scope.objectTypeId), attribute: name } },
      });
      return raw.attribute;
    }
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/** @public */
export const getDataIndexAttributeQueryOptions = (workspaceId: number, scope: AttributeScope, name: string) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeDetail(workspaceId, scope, name),
    queryFn: () => getDataIndexAttribute(workspaceId, scope, name),
    staleTime: STALE_TIME,
  });

type UseDataIndexAttributeOptions = {
  scope: AttributeScope;
  name: string;
  queryConfig?: QueryConfig<typeof getDataIndexAttributeQueryOptions>;
};

/** @public */
export const useDataIndexAttribute = ({ scope, name, queryConfig }: UseDataIndexAttributeOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getDataIndexAttributeQueryOptions(workspaceId, scope, name), ...queryConfig });
};

/** @public */
export async function getDataIndexEvent(workspaceId: number, name: string): Promise<DataIndexEvent> {
  const raw = await hydraUiApi.events.getEventNamesByEventName({
    params: { path: { ...environmentPath(workspaceId), event_name: name } },
  });
  return raw.event;
}

/** @public */
export const getDataIndexEventQueryOptions = (workspaceId: number, name: string) =>
  queryOptions({
    queryKey: dataIndexKeys.eventDetail(workspaceId, name),
    queryFn: () => getDataIndexEvent(workspaceId, name),
    staleTime: STALE_TIME,
  });

type UseDataIndexEventOptions = {
  name: string;
  queryConfig?: QueryConfig<typeof getDataIndexEventQueryOptions>;
};

/** @public */
export const useDataIndexEvent = ({ name, queryConfig }: UseDataIndexEventOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getDataIndexEventQueryOptions(workspaceId, name), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Usage and profile counts                                                   */
/* ------------------------------------------------------------------------- */

/**
 * How many campaigns, segments and so on reference each of the named
 * attributes. Asked for one page of names at a time: the counts are expensive
 * enough that the list endpoint does not carry them.
 *
 * Object and relationship attributes count only campaigns, segments and
 * newsletters — they cannot appear in content — so their responses are a
 * narrower shape than a profile attribute's.
 *
 * @public
 */
export async function getDataIndexAttributeUsage(
  workspaceId: number,
  scope: AttributeScope,
  names: readonly string[],
): Promise<Record<string, DataIndexAttributeUsage | DataIndexObjectAttributeUsage>> {
  const path = environmentPath(workspaceId);
  const body = { attributes: [...names] };

  switch (scope.kind) {
    case 'profile': {
      const raw = await hydraUiApi.attributes.usage({ params: { path }, body });
      return raw.attribute_usages;
    }
    case 'object': {
      const raw = await hydraUiApi.objectAttributes.usage({
        params: { path: { ...path, type_id: String(scope.objectTypeId) } },
        body,
      });
      return raw.attribute_usages;
    }
    case 'relationship': {
      const raw = await hydraUiApi.relationshipAttributes.objectRelationshipAttributesUsage({
        params: { path: { ...path, type_id: String(scope.objectTypeId) } },
        body,
      });
      return raw.attribute_usages;
    }
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/** @public */
export const getDataIndexAttributeUsageQueryOptions = (
  workspaceId: number,
  scope: AttributeScope,
  names: readonly string[],
) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeUsage(workspaceId, scope, names),
    queryFn: () => getDataIndexAttributeUsage(workspaceId, scope, names),
    staleTime: STALE_TIME,
  });

/** @public */
export async function getDataIndexEventUsage(
  workspaceId: number,
  names: readonly string[],
): Promise<Record<string, DataIndexEventUsage>> {
  const raw = await hydraUiApi.events.eventNamesUsage({
    params: { path: environmentPath(workspaceId) },
    body: { events: [...names] },
  });
  return raw.event_usages;
}

/** @public */
export const getDataIndexEventUsageQueryOptions = (workspaceId: number, names: readonly string[]) =>
  queryOptions({
    queryKey: dataIndexKeys.eventUsage(workspaceId, names),
    queryFn: () => getDataIndexEventUsage(workspaceId, names),
    staleTime: STALE_TIME,
  });

/**
 * How many people carry each of the named attributes. A name the response
 * omits has no count yet rather than a count of zero — the backend answers
 * within a budget and drops what it could not reach, so callers ask again for
 * what is missing rather than showing a wrong zero.
 *
 * @public
 */
export async function getDataIndexAttributeProfileCounts(
  workspaceId: number,
  names: readonly string[],
): Promise<Record<string, number>> {
  const raw = await hydraUiApi.attributes.profileCount({
    params: { path: environmentPath(workspaceId) },
    body: { attributes: [...names] },
  });
  return raw.attribute_profile_counts;
}

/** @public */
export const getDataIndexAttributeProfileCountsQueryOptions = (workspaceId: number, names: readonly string[]) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeProfileCounts(workspaceId, names),
    queryFn: () => getDataIndexAttributeProfileCounts(workspaceId, names),
    staleTime: STALE_TIME,
  });

/** How many people have sent each of the named events. Partial like the attribute counts. @public */
export async function getDataIndexEventProfileCounts(
  workspaceId: number,
  names: readonly string[],
): Promise<Record<string, number>> {
  const raw = await hydraUiApi.events.eventNamesProfileCount({
    params: { path: environmentPath(workspaceId) },
    body: { events: [...names] },
  });
  return raw.event_profile_counts;
}

/** @public */
export const getDataIndexEventProfileCountsQueryOptions = (workspaceId: number, names: readonly string[]) =>
  queryOptions({
    queryKey: dataIndexKeys.eventProfileCounts(workspaceId, names),
    queryFn: () => getDataIndexEventProfileCounts(workspaceId, names),
    staleTime: STALE_TIME,
  });

/**
 * How many objects of one type carry each of its attributes. Unlike the
 * profile counts this is not asked per name: the endpoint answers for every
 * attribute the object type has.
 *
 * @public
 */
export async function getDataIndexObjectCounts(
  workspaceId: number,
  objectTypeId: number,
): Promise<Record<string, number>> {
  const raw = await hydraUiApi.objectAttributes.objectCount({
    params: { path: { ...environmentPath(workspaceId), type_id: String(objectTypeId) } },
  });
  return raw.attribute_object_counts;
}

/** @public */
export const getDataIndexObjectCountsQueryOptions = (workspaceId: number, objectTypeId: number) =>
  queryOptions({
    queryKey: dataIndexKeys.attributeObjectCounts(workspaceId, objectTypeId),
    queryFn: () => getDataIndexObjectCounts(workspaceId, objectTypeId),
    staleTime: STALE_TIME,
  });
